//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  BatchUpdateModerationStatusRequest,
  GetContentCurationGuidelinesResponse,
  GuidelinesConfigType,
  RejectModerationMediaRequest,
  RejectionReasonsResponse,
  ShareModerationMediaRequest,
  UnshareModerationMediaRequest,
  UpdateContentCurationGuidelinesRequest,
} from '@apis/moderation/moderationApiModel';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const MODERATION_GATEWAY = 'moderation_gateway';
const MODERATION_BASE = '/api/v1/moderation';
const MANUAL_MODERATION_BASE = '/api/v1/manual-moderation';
const AI_MODERATION_BASE = '/api/v1/ai-moderation';

const moderationGatewayFetch = (
  basePath: string,
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    MODERATION_GATEWAY,
    `${basePath}${endpoint}`,
    init,
  );
};

export const getRejectionReasons = async (
  init?: RequestInit,
): Promise<RejectionReasonsResponse> => {
  const response = await moderationGatewayFetch(
    MANUAL_MODERATION_BASE,
    '/rejection-reasons',
    {
      ...init,
      method: 'GET',
    },
  );

  return response.json<RejectionReasonsResponse>();
};

export const rejectModerationMedia = async (
  req: RejectModerationMediaRequest,
  init?: RequestInit,
): Promise<void> => {
  await moderationGatewayFetch(MANUAL_MODERATION_BASE, '/shared-media/reject', {
    ...init,
    method: 'PATCH',
    body: JSON.stringify(req),
  });
};

export const shareModerationMedia = async (
  req: ShareModerationMediaRequest,
  init?: RequestInit,
): Promise<void> => {
  await moderationGatewayFetch(MANUAL_MODERATION_BASE, '/shared-media/share', {
    ...init,
    method: 'POST',
    body: JSON.stringify(req),
  });
};

export const unshareModerationMedia = async (
  req: UnshareModerationMediaRequest,
  init?: RequestInit,
): Promise<void> => {
  await moderationGatewayFetch(MANUAL_MODERATION_BASE, '/shared-media/unshare', {
    ...init,
    method: 'PATCH',
    body: JSON.stringify(req),
  });
};

export const getContentCurationGuidelines = async (
  configType?: GuidelinesConfigType,
  init?: RequestInit,
): Promise<GetContentCurationGuidelinesResponse> => {
  const params = new URLSearchParams();
  if (configType) {
    params.set('configType', configType);
  }
  const query = params.toString();
  const response = await moderationGatewayFetch(
    AI_MODERATION_BASE,
    `/guidelines${query ? `?${query}` : ''}`,
    { ...init, method: 'GET' },
  );
  return response.json<GetContentCurationGuidelinesResponse>();
};

export const updateContentCurationGuidelines = async (
  configType: GuidelinesConfigType,
  body: UpdateContentCurationGuidelinesRequest,
  init?: RequestInit,
): Promise<void> => {
  const params = new URLSearchParams({ configType });
  await moderationGatewayFetch(
    AI_MODERATION_BASE,
    `/guidelines?${params.toString()}`,
    {
      ...init,
      method: 'PUT',
      body: JSON.stringify(body),
    },
  );
};

/**
 * Batch update moderation status for content items.
 *
 * PATCH /api/v1/moderation/status
 *
 * Returns 204 No Content on success.
 */
export const batchUpdateModerationStatus = async (
  req: BatchUpdateModerationStatusRequest,
  init?: RequestInit,
): Promise<void> => {
  await moderationGatewayFetch(MODERATION_BASE, '/status', {
    ...init,
    method: 'PATCH',
    body: JSON.stringify(req),
  });
};
